import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateAccessGrant } from '../api/accessGrants'
import { useInvitationMutations } from '../api/multiOrg'
import { useRoles, type RoleView } from '../api/roles'
import { useCan, usePermissions, isSubsetOf } from '../contexts/PermissionsContext'
import { useLocale } from '../contexts/LocaleContext'
import { getLocalizedName } from '../lib/localization'
import { MEMBER_KINDS, rolesForKind, type MemberKind } from '../lib/member-kinds'
import { AssetPicker, isAssetPickerEscapeToClear } from './AssetPicker'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Checkbox } from './ui/checkbox'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select'

/**
 * The invite dialog, shared by the members settings page and the asset
 * Access tab's "Invite" button (spec 2026-09-05-03). It already supports
 * scoped roles (asset picker + optional end date): a scoped-only role
 * (resident, property_owner, provider) creates an access grant whose
 * invitation carries `guest` as the membership role, everything else is a
 * plain organization invitation (spec 2026-09-04-03, decision 10).
 *
 * When `defaultAsset` is set — the asset page's "Invite" button — the dialog
 * opens pre-scoped to that asset: the picker is pre-filled and the role
 * defaults to the first assignable scoped-only role instead of `member`, so
 * the asset picker section is visible immediately rather than hidden behind
 * a role change the caller would otherwise have to make themselves.
 *
 * The dialog asks the member kind first (spec 2026-10-06-04) and only offers
 * the roles of that kind: organization-wide roles for staff, `resident` and
 * `property_owner` for residents, `provider` for service providers. Residents
 * and providers are always invited through a grant on an asset.
 */
export function InviteMemberDialog({
    org,
    open,
    onOpenChange,
    defaultAsset,
    defaultKind,
    onInvited,
}: {
    org: string
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Pre-fills the asset picker and defaults the role to a scoped one. */
    defaultAsset?: { uid: string; name: string }
    /**
     * The kind preselected when the dialog opens: the members page passes
     * its current tab. Defaults to `resident` with an asset, `staff` without.
     */
    defaultKind?: MemberKind
    /** Called after a successful invite/grant, in addition to cache invalidation. */
    onInvited?: () => void
}) {
    const { t } = useTranslation('access')
    const { t: tRoles } = useTranslation('roles')
    const { locale } = useLocale()

    const invitationMutations = useInvitationMutations(org)
    const createGrant = useCreateAccessGrant(org)

    // Roles are org data, not a compile-time list: an organization can define
    // its own on /$org/settings/roles, so the picker below is fed by GET
    // /roles and labelled from the API's multilingual `name`.
    const canReadRoles = useCan('members.read')
    const { data: rolesData } = useRoles(org, canReadRoles)
    const roles = useMemo<RoleView[]>(() => rolesData?.data ?? [], [rolesData])
    const { permissions: myPermissions } = usePermissions()

    // A role the caller could not grant is offered but disabled: the backend
    // refuses it with 403 permission_escalation, and discovering that after
    // submitting is worse than seeing it greyed out.
    const assignableRoles = useMemo(
        () =>
            roles.map((r) => ({
                role: r,
                assignable: isSubsetOf(r.permissions, myPermissions),
            })),
        [roles, myPermissions]
    )

    const initialKind: MemberKind = defaultKind ?? (defaultAsset ? 'resident' : 'staff')
    const [kind, setKind] = useState<MemberKind>(initialKind)

    // The roles of the chosen kind only: the backend refuses the others with
    // 422 role_not_allowed_for_kind.
    const kindRoles = useMemo(
        () => assignableRoles.filter(({ role }) => rolesForKind(kind, [role]).length > 0),
        [assignableRoles, kind]
    )

    const defaultRoleCode = useMemo(() => {
        if (kind === 'staff') {
            const member = kindRoles.find(({ role, assignable }) => role.code === 'member' && assignable)
            return member?.role.code ?? kindRoles.find(({ assignable }) => assignable)?.role.code ?? 'member'
        }
        return kindRoles.find(({ assignable }) => assignable)?.role.code ?? ''
    }, [kind, kindRoles])

    const [email, setEmail] = useState('')
    const [role, setRole] = useState<string>(defaultRoleCode)
    const [assetUid, setAssetUid] = useState<string>(defaultAsset?.uid ?? '')
    const [assetName, setAssetName] = useState<string>(defaultAsset?.name ?? '')
    const [validUntil, setValidUntil] = useState('')
    const [error, setError] = useState<string | null>(null)
    // A staff member can be narrowed to one asset with an ordinary role: an
    // agency director is `member` on their agency (spec 2026-10-10-04). On by
    // default when the dialog is opened from an asset.
    const [limitToAsset, setLimitToAsset] = useState<boolean>(Boolean(defaultAsset))

    // Reset the form on the closed -> open transition only, so a role list
    // that finishes loading while the dialog is already open never wipes out
    // what the caller has typed.
    const wasOpen = useRef(false)
    // Whether the caller picked a role in this opening. Until they do, the role
    // follows the default: opened before GET /roles answered, the dialog would
    // otherwise keep `member` and hide the asset picker of a pre-scoped invite.
    const roleTouched = useRef(false)
    useEffect(() => {
        if (open && !wasOpen.current) {
            roleTouched.current = false
            setEmail('')
            setKind(initialKind)
            setRole(defaultRoleCode)
            setAssetUid(defaultAsset?.uid ?? '')
            setAssetName(defaultAsset?.name ?? '')
            setValidUntil('')
            setLimitToAsset(Boolean(defaultAsset))
            setError(null)
        }
        wasOpen.current = open
    }, [open, defaultRoleCode, defaultAsset, initialKind])
    useEffect(() => {
        if (open && !roleTouched.current) setRole(defaultRoleCode)
    }, [open, defaultRoleCode])

    const roleLabel = (code?: string) => {
        if (!code) return '—'
        const found = roles.find((r) => r.code === code)
        if (found) return getLocalizedName(found.name as Record<string, string>, locale) || code
        return code
    }

    const selectedRole = roles.find((r) => r.code === role)
    // Residents and providers always join through a grant; staff only when
    // they are given a scoped-only role, which their kind cannot hold anyway.
    const roleIsScopedOnly = Boolean(selectedRole?.scoped_only)
    const isScoped = kind !== 'staff' || roleIsScopedOnly || limitToAsset
    const isPending = invitationMutations.create.isPending || createGrant.isPending

    const handleInvite = async () => {
        setError(null)
        try {
            if (isScoped) {
                if (!assetUid) {
                    setError(t('invite.scoped_role_needs_asset'))
                    return
                }
                await createGrant.mutateAsync({
                    params: { path: { orgCode: org } },
                    body: {
                        email: email.trim(),
                        role_code: role,
                        asset_uid: assetUid,
                        kind,
                        ...(validUntil ? { valid_until: new Date(validUntil).toISOString() } : {}),
                    },
                })
            } else {
                await invitationMutations.create.mutateAsync({
                    email: email.trim(),
                    role_code: role,
                    kind,
                })
            }
            onOpenChange(false)
            onInvited?.()
        } catch (err) {
            setError(parseErrorTitle(err))
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="invite-member-dialog"
                onEscapeKeyDown={(event) => {
                    // Let Escape clear the asset picker's search term first
                    // (Radix would otherwise close the dialog because its
                    // Escape listener runs in document-capture, before the
                    // picker's own Input onKeyDown) — discarding whatever
                    // else the form holds (email, role, valid-until).
                    if (isAssetPickerEscapeToClear(event)) event.preventDefault()
                }}
            >
                <DialogHeader>
                    <DialogTitle>{t('invite.title')}</DialogTitle>
                    <DialogDescription>{t('invite.description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="invite-kind">{t('invite.kind')}</Label>
                        <Select
                            value={kind}
                            onValueChange={(value) => {
                                roleTouched.current = false
                                setKind(value as MemberKind)
                            }}
                        >
                            <SelectTrigger id="invite-kind" data-testid="invite-kind-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {MEMBER_KINDS.map((k) => (
                                    <SelectItem key={k} value={k} data-testid={`invite-kind-option-${k}`}>
                                        {t(`kinds.${k}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="invite-email">{t('invite.email')}</Label>
                        <Input
                            id="invite-email"
                            type="email"
                            placeholder={t('invite.email_placeholder')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="invite-email-input"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="invite-role">{t('invite.role')}</Label>
                        <Select
                            value={role}
                            onValueChange={(code) => {
                                roleTouched.current = true
                                setRole(code)
                            }}
                        >
                            <SelectTrigger id="invite-role" data-testid="invite-role-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {kindRoles.map(({ role: r, assignable }) => (
                                    <SelectItem
                                        key={r.uid}
                                        value={r.code}
                                        disabled={!assignable}
                                        title={assignable ? undefined : tRoles('assign.not_assignable')}
                                        data-testid={`invite-role-option-${r.code}`}
                                    >
                                        {roleLabel(r.code)}
                                        {!assignable && (
                                            <span className="ml-2 text-xs text-muted-foreground">
                                                {tRoles('assign.not_assignable')}
                                            </span>
                                        )}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {kind === 'staff' && !roleIsScopedOnly && (
                        <div className="flex items-start gap-2">
                            <Checkbox
                                id="invite-limit-to-asset"
                                checked={limitToAsset}
                                onCheckedChange={(checked) => setLimitToAsset(checked === true)}
                                data-testid="invite-limit-to-asset"
                            />
                            <div className="space-y-0.5">
                                <Label htmlFor="invite-limit-to-asset">{t('invite.limit_to_asset')}</Label>
                                <p className="text-xs text-muted-foreground">{t('invite.limit_to_asset_help')}</p>
                            </div>
                        </div>
                    )}
                    {isScoped && (
                        <>
                            <div className="space-y-2">
                                <Label>{t('invite.asset')}</Label>
                                <AssetPicker
                                    org={org}
                                    value={assetUid}
                                    valueLabel={assetName}
                                    onChange={(uid, name) => {
                                        setAssetUid(uid)
                                        setAssetName(name)
                                    }}
                                    testId="invite-asset-picker"
                                    help={t('invite.asset_help')}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="invite-valid-until">{t('invite.valid_until')}</Label>
                                <Input
                                    id="invite-valid-until"
                                    type="date"
                                    value={validUntil}
                                    onChange={(e) => setValidUntil(e.target.value)}
                                    data-testid="invite-valid-until-input"
                                />
                                <p className="text-xs text-muted-foreground">
                                    {t('invite.valid_until_help')}
                                </p>
                            </div>
                        </>
                    )}
                    {error && (
                        <p className="text-sm text-destructive" data-testid="invite-error">
                            {error}
                        </p>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="invite-cancel-btn"
                    >
                        {t('invite.cancel')}
                    </Button>
                    <Button
                        onClick={handleInvite}
                        disabled={!email.trim() || !role || isPending}
                        data-testid="invite-submit-btn"
                    >
                        {isPending ? t('invite.sending') : t('invite.submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

// parseErrorTitle pulls the human-readable title out of an API error without
// leaking the raw JSON. The backend returns `{title, code}` per
// base.ErrorResponse — if we can parse it we show the title, otherwise we
// fall back to the raw string.
function parseErrorTitle(err: unknown): string {
    if (!err) return ''
    const text = err instanceof Error ? err.message : String(err)
    try {
        const parsed = JSON.parse(text)
        if (parsed && typeof parsed.title === 'string') return parsed.title
    } catch {
        // not JSON — fall through
    }
    return text
}
